/**
 * W3C traceparent for cross-device / sign-on-mobile QR handoff (Alternative A).
 * Host embeds context in the link; guest continues the same traceId.
 */
import {
  context,
  trace,
  TraceFlags,
  isSpanContextValid
} from '@opentelemetry/api';

const TRACEPARENT_RE = /^([\da-f]{2})-([\da-f]{32})-([\da-f]{16})-([\da-f]{2})$/i;

/**
 * @param {import('@opentelemetry/api').SpanContext} spanContext
 * @returns {string}
 */
export function formatTraceparent(spanContext) {
  if (!spanContext || !isSpanContextValid(spanContext)) {
    return '';
  }
  const flags = (spanContext.traceFlags & 0xff).toString(16).padStart(2, '0');
  return `00-${spanContext.traceId}-${spanContext.spanId}-${flags}`;
}

/**
 * @param {string} value
 * @returns {import('@opentelemetry/api').SpanContext | null}
 */
export function parseTraceparent(value) {
  const raw = String(value || '').trim();
  const m = raw.match(TRACEPARENT_RE);
  if (!m) {
    return null;
  }
  const spanContext = {
    traceId: m[2].toLowerCase(),
    spanId: m[3].toLowerCase(),
    traceFlags: parseInt(m[4], 16),
    isRemote: true
  };
  if (!isSpanContextValid(spanContext)) {
    return null;
  }
  // Prefer sampled so guest work is recorded when host was sampled.
  if ((spanContext.traceFlags & TraceFlags.SAMPLED) === 0) {
    spanContext.traceFlags = TraceFlags.SAMPLED;
  }
  return spanContext;
}

/**
 * Read `traceparent` from Paperfly handoff URLs (`/do=id&traceparent=…`).
 * @param {string} [href]
 * @returns {import('@opentelemetry/api').SpanContext | null}
 */
export function readTraceparentFromHref(href) {
  const url =
    href || (typeof window !== 'undefined' ? window.location.href : '');
  if (!url) return null;
  const m = String(url).match(/[?&]traceparent=([^&#]*)/i);
  if (!m || !m[1]) return null;
  try {
    return parseTraceparent(decodeURIComponent(m[1]));
  } catch (e) {
    return parseTraceparent(m[1]);
  }
}

/**
 * Append `&traceparent=` for QR / data-testid links (Paperfly uses `&` after id).
 * @param {string} url
 * @param {import('@opentelemetry/api').SpanContext | null | undefined} spanContext
 */
export function withTraceparent(url, spanContext) {
  if (!url) return url;
  if (/[?&]traceparent=/i.test(url)) return url;
  const tp = formatTraceparent(spanContext);
  if (!tp) return url;
  return `${url}&traceparent=${tp}`;
}

/**
 * Parent context for guest `pwa.session` when URL carries a remote parent.
 * @returns {import('@opentelemetry/api').Context}
 */
export function getRemoteParentContext(href) {
  const remote = readTraceparentFromHref(href);
  if (!remote) {
    return context.active();
  }
  return trace.setSpanContext(context.active(), remote);
}
